import { Control, useWatch } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../../ui/components/ui/form';
import { Input } from '../../../../ui/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../ui/components/ui/select';
import { PAYMENT_METHOD_LABEL } from '../../../offer-checkout/constants/payment-method';
import { PaymentMethod } from '../../../offer-checkout/constants/payment-method.enum';
import { formatDate } from '../../../offer-checkout/lib/format';
import { NO_SELECTION } from '../../constants/offers-page.constants';
import OfferFormSection, { CollapsiblePanelProps } from '../offer-form-section/offer-form-section';
import { OfferFormValues } from '../offer-form/offer-form.schema';

export interface OfferPaymentSectionProps {
  control: Control<OfferFormValues>;
  /** Every 1st/15th of the next months, from `GET offers/admin/meta` – on a correction plus the contract's current start date. */
  startDateOptions: string[];
  panel: CollapsiblePanelProps;
  /** The contract data of an accepted offer: the start date is required and SEPA asks for the bank details. */
  isCorrection?: boolean;
}

/** Payment method and the start date – a prefill for the customer, or on a correction the contract's. */
const OfferPaymentSection = ({ control, startDateOptions, panel, isCorrection = false }: OfferPaymentSectionProps) => {
  const paymentMethod = useWatch({ control, name: 'paymentMethod' });

  return (
    <OfferFormSection title="Zahlung & Start" panel={panel}>
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={control}
          name="paymentMethod"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-[10px]">
              <FormLabel className="text-[14px] font-bold text-[#12282A]">Zahlungsart *</FormLabel>
              <Select value={field.value ?? ''} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Zahlungsart wählen" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {Object.values(PaymentMethod).map((method) => (
                    <SelectItem key={method} value={method}>
                      {PAYMENT_METHOD_LABEL[method]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[12px] text-[#888]">
                {isCorrection ? 'Bei Rechnung werden IBAN und BIC entfernt.' : 'SEPA benötigt eine Eingabe der Bankdaten des Kunden.'}
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="customer.newCustomerDate"
          render={({ field }) => (
            <FormItem className="flex flex-col gap-[10px]">
              <FormLabel className="text-[14px] font-bold text-[#12282A]">{isCorrection ? 'Startdatum *' : 'Startdatum'}</FormLabel>
              <Select
                value={field.value || (isCorrection ? '' : NO_SELECTION)}
                onValueChange={(value) => field.onChange(value === NO_SELECTION ? '' : value)}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Startdatum wählen" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {!isCorrection && <SelectItem value={NO_SELECTION}>Kunde wählt selbst</SelectItem>}
                  {startDateOptions.map((date) => (
                    <SelectItem key={date} value={date}>
                      {formatDate(date)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[12px] text-[#888]">
                {isCorrection ? 'Das aktuelle Startdatum oder ein kommender 1./15.' : 'Änderbar für den Kunden.'}
              </p>
              <FormMessage />
            </FormItem>
          )}
        />

        {isCorrection &&
          paymentMethod === PaymentMethod.SEPA &&
          (['iban', 'bic'] as const).map((name) => (
            <FormField
              key={name}
              control={control}
              name={`customer.${name}`}
              render={({ field }) => (
                <FormItem className="flex flex-col gap-[10px]">
                  <FormLabel className="text-[14px] font-bold text-[#12282A]">{name === 'iban' ? 'IBAN *' : 'BIC *'}</FormLabel>
                  <FormControl>
                    <Input {...field} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
      </div>
    </OfferFormSection>
  );
};

export default OfferPaymentSection;
